import { useCallback, useEffect, useState } from 'react'
import useEventListener from '../useEventListener'
import isIn from '../isIn'
import convertEventMessageToJSON from '../convertEventMessageToJSON/convertEventMessageToJSON'

const VALID_PLAYER_STATE_RESPONSE = [
  'unlock',
  'lockout',
  'registered-to-attend',
  'relocate-player',
  'ready-to-play',
  'processing'
]

const VALID_PLAYER_PAYLOAD_RESPONSE = [
  'Registration',
  'Survey',
  'Login'
]

enum PlayerEvent {
  'Registration' = 'registrationRequired',
  'Survey' = 'surveyRequired',
  'ready-to-play' = 'playerReady',
  'registered-to-attend' = 'registrationComplete',
  'processing' = 'registrationComplete',
  'relocate-player' = 'main',
  'lockout' = 'loading',
  'Login' = 'loginRequired'
}

type PlayerEventState = 'Survey' | 'Registration' | 'lockout' | 'registered-to-attend' | 'relocate-player' | 'ready-to-play' | 'Login' | 'processing'

export type RegistrationState =
  'awaiting' | 'registrationRequired' | 'surveyRequired' | 'registrationComplete' | 'loading' | 'playerReady' | 'ready' | 'loginRequired'
export const REGISTRATION_STATE_VALUES: RegistrationState[] = [
  'awaiting', 'registrationRequired', 'surveyRequired', 'registrationComplete', 'loading', 'playerReady', 'ready', 'loginRequired'
]
export const REGISTRATION_OVERLAY_VALUES = [
  true, false
]

/**
 * Is the user registered
 * @param registrationState 
 */
export function isRegistered (registrationState: RegistrationState): boolean {

  return isIn<RegistrationState>(registrationState, 'registrationComplete', 'playerReady', 'ready')

}

/**
 * Is the player ready to show in the main section of the page
 * @param registrationState 
 */
export function isPlayerReady (registrationState: RegistrationState): boolean {

  return registrationState === 'ready'

}

export interface UsePlayerState {
  registrationState: RegistrationState
  setPlayerReady(): void
  registrationOverlay: boolean
  setRegistrationOverlay: (registrationOverlay: boolean) => void
  setRegistrationState?: (registrationState: RegistrationState) => void
}

export const defaultState: UsePlayerState = {
  registrationState: 'awaiting',
  setPlayerReady: () => null,
  registrationOverlay: false,
  setRegistrationOverlay: () => null,
  setRegistrationState: () => null
}

/**
 * Determines whether or not it's a message we care about, then converts it to a player event
 * @param playerState 
 * @param payload 
 */
function getValidMessage (playerState?: string, payload?: string): PlayerEventState {

  if (!playerState) return
  if (!VALID_PLAYER_STATE_RESPONSE.some(state => state === playerState)) return
  if (playerState === 'unlock' && !VALID_PLAYER_PAYLOAD_RESPONSE.some(_payload => _payload === payload)) return

  return (playerState === 'unlock')
    ? payload as PlayerEventState
    : playerState as PlayerEventState

}

/**
 * Takes the inbound message and returns a player event if one is present
 * @param event 
 */
function filterPlayerMessage (event): PlayerEventState {

  const JSONData = convertEventMessageToJSON(event?.data)
  
  if (!JSONData) return
  return getValidMessage(JSONData?.body['player-state'], JSONData?.body?.payload)

}

const usePlayerState = (): UsePlayerState => {

  const [ isComplete, setIsComplete ] = useState(false)
  const [ message, setMessage ] = useState<PlayerEventState>()
  const [ registrationState, setRegistrationState ] = useState<RegistrationState>(() => defaultState.registrationState)
  const [ registrationOverlay, setRegistrationOverlay ] = useState<boolean>(defaultState.registrationOverlay)

  const setPlayerReady = useCallback(() => {
  
    setRegistrationState('ready')
    setIsComplete(true)
  
  }, [ setRegistrationState, setIsComplete ])

  useEffect(() => {
    return () => {
      if (registrationOverlay) {
        document.removeEventListener('click', clickOutside, true)
        const smartform = document.querySelector('globalauth-helpercomponent')
        if (smartform) {
          smartform.removeEventListener('click', clickOutside, true)
        }
        // const iframe = document.getElementById('portal-authenticator')
        // if (iframe && (<HTMLIFrameElement> iframe).contentDocument) {
        //   (<HTMLIFrameElement> iframe).contentDocument.body.removeEventListener('click', clickOutside, true)
        // }
      }
    }
  }, [])

  useEffect(() => {
    if (registrationOverlay && !isRegistered(registrationState)) {  
      setRegistrationOverlay(true)
      document.addEventListener('click', clickOutside, true)
      const smartform = document.querySelector('globalauth-helpercomponent')
      if (smartform) {
        smartform.addEventListener('click', clickOutside, true)
      }
      // const iframe = document.getElementById('portal-authenticator')
      // if (iframe && (<HTMLIFrameElement> iframe).contentDocument) {
      //   (<HTMLIFrameElement> iframe).contentDocument.body.addEventListener('click', clickOutside, true) 
      // }
    }
  }, [registrationOverlay])

  useEffect(() => {

    if (isComplete || !message) return

    switch (message) {

      case 'Registration': case 'ready-to-play':
      case 'lockout': case 'Login': case 'processing':
        setRegistrationState(PlayerEvent[message])
        break
      default:
        break

    }

  }, [ message, isComplete ])

  const clickOutside = (e) => {
    const clickedOnDefault = e.target.closest(`[data-overlay='registration-thumbnail']`) || e.target.closest(`[data-overlay='text-with-icon-click']`)
    if (!clickedOnDefault) {
      setRegistrationOverlay(false)
      document.removeEventListener('click', clickOutside, true)
      const smartform = document.querySelector('globalauth-helpercomponent')
      if (smartform) {
        smartform.removeEventListener('click', clickOutside, true)
      }
      // const iframe = document.getElementById('portal-authenticator')
      // if (iframe && (<HTMLIFrameElement> iframe).contentDocument) {
      //   (<HTMLIFrameElement> iframe).contentDocument.body.removeEventListener('click', clickOutside, true) 
      // }
    }
  }

  const handleMessage = useCallback(event => {
    if (isComplete) return

    const newPlayerState = filterPlayerMessage(event)
    if (!newPlayerState) return
    setMessage(newPlayerState)

  }, [ setMessage, isComplete ])

  useEventListener('message', handleMessage)

  return {
    registrationState,
    setPlayerReady,
    registrationOverlay,
    setRegistrationOverlay,
    setRegistrationState
  }

}

export default usePlayerState
